import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ContentStatusBadge } from '@tms/shared-ui';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import {
  createChallenge,
  deleteChallenge,
  getBadges,
  getChallenges,
  toggleChallengeActive,
  updateChallenge,
  type Badge,
  type Challenge,
  type ChallengeCondition,
} from '../services/progressionAdminApi';
import { listDocuments, type DocumentRow } from '../services/documentApi';
import { BadgeIconField, isDefaultBadgeIcon, resolveBadgeIconSrc, ADMIN_BADGE_ICON_SIZE } from './BadgeIconField';
import {
  ProgressionLocaleFields,
  emptyProgressionLocales,
  getProgressionDisplayName,
  progressionLocalesFromEntity,
  progressionPayloadFromLocales,
  type ProgressionLocalesForm,
} from './ProgressionLocaleFields';

const CONDITION_TYPES = ['complete_documents', 'complete_count_in_group', 'complete_count_in_period', 'social_friends', 'admin_verification'] as const;

const CONDITION_TYPE_LABELS: Record<(typeof CONDITION_TYPES)[number], string> = {
  complete_documents: 'Complete all documents',
  complete_count_in_group: 'Complete N from document group',
  complete_count_in_period: 'Complete count in period',
  social_friends: 'Social — minimum friends',
  admin_verification: 'Social — admin verification (manual)',
};
const DOC_DOMAINS = ['ST', 'ACC', 'MD', 'M', 'SONG', 'EX', 'Other'];
const PERIODS = ['rolling_days', 'calendar_week', 'calendar_month', 'challenge_window'] as const;

type DocRow = { documentCode: string; minProgression: string };

type ConditionForm = {
  type: (typeof CONDITION_TYPES)[number];
  minProgression: string;
  documents: DocRow[];
  itemKind: 'documents' | 'exercises';
  count: string;
  period: (typeof PERIODS)[number];
  periodDays: string;
  filterDomain: string;
  filterDocType: string;
  minFriends: string;
  verificationKind: string;
};

const emptyDocRow = (): DocRow => ({ documentCode: '', minProgression: '' });

const emptyCondition = (type: ConditionForm['type'] = 'complete_documents'): ConditionForm => ({
  type,
  minProgression: '75',
  documents: [emptyDocRow()],
  itemKind: 'documents',
  count: '1',
  period: 'rolling_days',
  periodDays: '7',
  filterDomain: '',
  filterDocType: '',
  minFriends: '1',
  verificationKind: 'google_review',
});

const emptyForm = () => ({
  locales: emptyProgressionLocales(),
  icon: '',
  isActive: true,
  conditionMode: 'all' as 'all' | 'any',
  startsAt: '',
  endsAt: '',
  rewardXp: '0',
  rewardBadgeIds: [] as string[],
  conditions: [emptyCondition()],
});

function conditionSummary(c: ChallengeCondition): string {
  if (c.type === 'complete_documents') {
    const n = c.documents?.length ?? 0;
    return `Complete all ${n} document(s)`;
  }
  if (c.type === 'complete_count_in_group') {
    const n = c.documents?.length ?? 0;
    return `Complete ${c.count ?? 1} of ${n} in group`;
  }
  if (c.type === 'complete_count_in_period') {
    return `${c.count ?? 1} ${c.itemKind || 'documents'} (${c.period || 'rolling_days'})`;
  }
  if (c.type === 'social_friends') {
    return `${c.minFriends ?? 1}+ friends`;
  }
  if (c.type === 'admin_verification') {
    return `Admin verification${c.verificationKind ? `: ${c.verificationKind}` : ''}`;
  }
  return 'unknown condition';
}

function conditionFromChallenge(c: ChallengeCondition): ConditionForm {
  if (c.type === 'complete_count_in_period') {
    return {
      ...emptyCondition('complete_count_in_period'),
      itemKind: c.itemKind || 'documents',
      count: String(c.count ?? 1),
      period: (c.period as ConditionForm['period']) || 'rolling_days',
      periodDays: String(c.periodDays ?? 7),
      minProgression: String(c.minProgression ?? 75),
      filterDomain: c.filters?.docDomain || '',
      filterDocType: c.filters?.docType || '',
    };
  }
  if (c.type === 'social_friends') {
    return {
      ...emptyCondition('social_friends'),
      minFriends: String(c.minFriends ?? 1),
    };
  }
  if (c.type === 'admin_verification') {
    return {
      ...emptyCondition('admin_verification'),
      verificationKind: c.verificationKind || 'google_review',
    };
  }
  if (c.type === 'complete_count_in_group') {
    return {
      ...emptyCondition('complete_count_in_group'),
      count: String(c.count ?? 1),
      minProgression: String(c.minProgression ?? 75),
      documents: (c.documents?.length ? c.documents : [{ documentCode: '' }]).map((d) => ({
        documentCode: d.documentCode || '',
        minProgression: d.minProgression != null ? String(d.minProgression) : '',
      })),
    };
  }
  return {
    ...emptyCondition('complete_documents'),
    minProgression: String(c.minProgression ?? 75),
    documents: (c.documents?.length ? c.documents : [{ documentCode: '' }]).map((d) => ({
      documentCode: d.documentCode || '',
      minProgression: d.minProgression != null ? String(d.minProgression) : '',
    })),
  };
}

function buildConditionPayload(c: ConditionForm): ChallengeCondition {
  if (c.type === 'complete_count_in_period') {
    return {
      type: 'complete_count_in_period',
      itemKind: c.itemKind,
      count: parseInt(c.count, 10) || 1,
      period: c.period,
      periodDays: parseInt(c.periodDays, 10) || 7,
      minProgression: parseInt(c.minProgression, 10) || 75,
      filters: {
        ...(c.filterDomain && { docDomain: c.filterDomain }),
        ...(c.filterDocType.trim() && { docType: c.filterDocType.trim() }),
      },
    };
  }
  if (c.type === 'social_friends') {
    return {
      type: 'social_friends',
      minFriends: parseInt(c.minFriends, 10) || 1,
    };
  }
  if (c.type === 'admin_verification') {
    return {
      type: 'admin_verification',
      verificationKind: c.verificationKind.trim() || 'manual',
    };
  }
  const docs = c.documents.filter((d) => d.documentCode.trim());
  if (c.type === 'complete_count_in_group') {
    return {
      type: 'complete_count_in_group',
      count: parseInt(c.count, 10) || 1,
      minProgression: parseInt(c.minProgression, 10) || 75,
      documents: docs.map((d, i) => ({
        documentCode: d.documentCode.trim(),
        ...(d.minProgression !== '' && { minProgression: parseInt(d.minProgression, 10) }),
        order: i,
      })),
    };
  }
  return {
    type: 'complete_documents',
    minProgression: parseInt(c.minProgression, 10) || 75,
    documents: docs.map((d, i) => ({
      documentCode: d.documentCode.trim(),
      ...(d.minProgression !== '' && { minProgression: parseInt(d.minProgression, 10) }),
      order: i,
    })),
  };
}

type ChallengesAdminTabProps = {
  onMessage: (msg: string) => void;
  onListChange?: (count: number) => void;
};

const DocumentCodeField: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => {
  const [options, setOptions] = useState<DocumentRow[]>([]);
  const [input, setInput] = useState(value);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInput(value);
  }, [value]);

  const search = useCallback(async (q: string) => {
    if (!q || q.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await listDocuments({ search: q, limit: 20 });
      setOptions(res.data?.documents || []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Autocomplete
      size="small"
      freeSolo
      options={options}
      loading={loading}
      inputValue={input}
      onInputChange={(_, v) => {
        setInput(v);
        onChange(v);
        search(v);
      }}
      onChange={(_, opt) => {
        const code = typeof opt === 'string' ? opt : opt?.code || '';
        setInput(code);
        onChange(code);
      }}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : `${opt.code || ''} — ${opt.title || ''}`)}
      renderInput={(params) => <TextField {...params} label="Document code" />}
      sx={{ flex: 2, minWidth: 200 }}
    />
  );
};

export const ChallengesAdminTab: React.FC<ChallengesAdminTabProps> = ({ onMessage, onListChange }) => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; editing?: Challenge }>({ open: false });
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [chRes, bRes] = await Promise.all([getChallenges(), getBadges()]);
      const nextChallenges = chRes.data?.challenges || [];
      setChallenges(nextChallenges);
      onListChange?.(nextChallenges.length);
      setBadges(bRes.data?.badges || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load challenges');
    } finally {
      setLoading(false);
    }
  }, [onListChange]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (ch?: Challenge) => {
    if (!ch) {
      setForm(emptyForm());
      setDialog({ open: true });
      return;
    }
    const badgeIds = (ch.rewards?.badgeIds || []).map((b) => (typeof b === 'string' ? b : b._id));
    setForm({
      locales: progressionLocalesFromEntity(ch),
      icon: ch.icon && !isDefaultBadgeIcon(ch.icon) ? ch.icon : '',
      isActive: ch.isActive !== false,
      conditionMode: ch.conditionMode || 'all',
      startsAt: ch.startsAt ? ch.startsAt.slice(0, 16) : '',
      endsAt: ch.endsAt ? ch.endsAt.slice(0, 16) : '',
      rewardXp: String(ch.rewards?.experiencePoints ?? 0),
      rewardBadgeIds: badgeIds,
      conditions: (ch.conditions?.length
        ? ch.conditions
        : [{ type: 'complete_documents' as const, documents: [] }]
      ).map(conditionFromChallenge),
    });
    setDialog({ open: true, editing: ch });
  };

  const handleSave = async () => {
    const conditions = form.conditions.map(buildConditionPayload);
    const body = {
      ...progressionPayloadFromLocales(form.locales),
      icon: form.icon || undefined,
      isActive: form.isActive,
      conditionMode: form.conditionMode,
      ...(form.startsAt && { startsAt: new Date(form.startsAt).toISOString() }),
      ...(form.endsAt && { endsAt: new Date(form.endsAt).toISOString() }),
      conditions,
      rewards: {
        experiencePoints: parseInt(form.rewardXp, 10) || 0,
        badgeIds: form.rewardBadgeIds,
      },
    };

    try {
      if (dialog.editing) {
        await updateChallenge(dialog.editing._id, body);
        onMessage('Challenge updated.');
      } else {
        await createChallenge(body);
        onMessage('Challenge created.');
      }
      setDialog({ open: false });
      await load();
    } catch (e: any) {
      onMessage(e?.message || 'Save failed');
    }
  };

  if (loading) {
    return <Typography color="text.secondary">Loading challenges…</Typography>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => openEdit()}>
          Add challenge
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{'color':'primary.main'}}>Icon</TableCell>
            <TableCell sx={{'color':'primary.main'}}>Name</TableCell>
            <TableCell sx={{'color':'primary.main'}}>Conditions</TableCell>
            <TableCell sx={{'color':'primary.main'}}>Reward XP</TableCell>
            <TableCell sx={{'color':'primary.main'}}>Active</TableCell>
            <TableCell align="right" sx={{'color':'primary.main'}}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {challenges.map((ch) => {
            const src = resolveBadgeIconSrc(ch.icon);
            return (
              <TableRow key={ch._id}>
                <TableCell>
                  {src ? (
                    <Box
                      component="img"
                      src={src}
                      alt=""
                      sx={{
                        width: ADMIN_BADGE_ICON_SIZE,
                        height: ADMIN_BADGE_ICON_SIZE,
                        objectFit: 'contain',
                        bgcolor: 'transparent',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">default</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {getProgressionDisplayName(ch, 'en')}
                  </Typography>
                  {ch.locales?.fr?.name ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      FR: {ch.locales.fr.name}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  {(ch.conditions || []).map((c, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {conditionSummary(c)}
                    </Typography>
                  ))}
                  <Typography variant="caption" color="text.secondary">
                    mode: {ch.conditionMode || 'all'}
                  </Typography>
                </TableCell>
                <TableCell>{ch.rewards?.experiencePoints ?? 0}</TableCell>
                <TableCell>
                  {ch.isActive !== false ? <ContentStatusBadge status="active" /> : '—'}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    title={ch.isActive !== false ? 'Deactivate' : 'Activate'}
                    onClick={async () => {
                      await toggleChallengeActive(ch._id, ch.isActive === false);
                      onMessage('Challenge status updated.');
                      await load();
                    }}
                  >
                    {ch.isActive !== false ? <ToggleOnIcon fontSize="small" color="primary" /> : <ToggleOffIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" onClick={() => openEdit(ch)}>
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={async () => {
                      if (!window.confirm('Delete challenge?')) return;
                      try {
                        await deleteChallenge(ch._id);
                        onMessage('Challenge deleted.');
                        await load();
                      } catch (e: any) {
                        onMessage(e?.message || 'Delete failed');
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.editing ? 'Edit challenge' : 'Create challenge'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <ProgressionLocaleFields
              locales={form.locales}
              onChange={(locales: ProgressionLocalesForm) => setForm((f) => ({ ...f, locales }))}
            />
            <BadgeIconField icon={form.icon} onChange={(icon) => setForm((f) => ({ ...f, icon }))} label="Challenge icon" />
            <TextField
              select
              label="Active"
              value={form.isActive ? 'yes' : 'no'}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'yes' }))}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
            <TextField
              select
              label="Condition mode"
              value={form.conditionMode}
              onChange={(e) => setForm((f) => ({ ...f, conditionMode: e.target.value as 'all' | 'any' }))}
              helperText="all = every condition must pass; any = at least one"
            >
              <MenuItem value="all">All conditions (AND)</MenuItem>
              <MenuItem value="any">Any condition (OR)</MenuItem>
            </TextField>
            <Box display="flex" gap={1} flexWrap="wrap">
              <TextField
                label="Starts at"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Ends at"
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200 }}
              />
            </Box>

            <Typography variant="subtitle2">Conditions</Typography>
            {form.conditions.map((cond, cIdx) => (
              <Paper key={cIdx} variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" gap={1} alignItems="center" mb={1}>
                  <TextField
                    select
                    size="small"
                    label="Type"
                    value={cond.type}
                    onChange={(e) => {
                      const conditions = [...form.conditions];
                      conditions[cIdx] = emptyCondition(e.target.value as ConditionForm['type']);
                      setForm((f) => ({ ...f, conditions }));
                    }}
                    sx={{ minWidth: 220 }}
                  >
                    {CONDITION_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{CONDITION_TYPE_LABELS[t]}</MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={form.conditions.length <= 1}
                    onClick={() => setForm((f) => ({ ...f, conditions: f.conditions.filter((_, i) => i !== cIdx) }))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {cond.type === 'complete_documents' || cond.type === 'complete_count_in_group' ? (
                  <>
                    {cond.type === 'complete_count_in_group' ? (
                      <TextField
                        size="small"
                        label="Required count"
                        type="number"
                        value={cond.count}
                        onChange={(e) => {
                          const conditions = [...form.conditions];
                          conditions[cIdx] = { ...cond, count: e.target.value };
                          setForm((f) => ({ ...f, conditions }));
                        }}
                        helperText="How many documents from the group must be completed"
                        sx={{ width: 160, mb: 1 }}
                      />
                    ) : null}
                    <TextField
                      size="small"
                      label="Default min %"
                      type="number"
                      value={cond.minProgression}
                      onChange={(e) => {
                        const conditions = [...form.conditions];
                        conditions[cIdx] = { ...cond, minProgression: e.target.value };
                        setForm((f) => ({ ...f, conditions }));
                      }}
                      sx={{ width: 120, mb: 1 }}
                    />
                    {cond.documents.map((d, dIdx) => (
                      <Box key={dIdx} display="flex" gap={1} alignItems="center" mb={1}>
                        <DocumentCodeField
                          value={d.documentCode}
                          onChange={(documentCode) => {
                            const conditions = [...form.conditions];
                            const docs = [...cond.documents];
                            docs[dIdx] = { ...docs[dIdx], documentCode };
                            conditions[cIdx] = { ...cond, documents: docs };
                            setForm((f) => ({ ...f, conditions }));
                          }}
                        />
                        <TextField
                          size="small"
                          label="Min %"
                          type="number"
                          value={d.minProgression}
                          onChange={(e) => {
                            const conditions = [...form.conditions];
                            const docs = [...cond.documents];
                            docs[dIdx] = { ...docs[dIdx], minProgression: e.target.value };
                            conditions[cIdx] = { ...cond, documents: docs };
                            setForm((f) => ({ ...f, conditions }));
                          }}
                          sx={{ width: 80 }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const conditions = [...form.conditions];
                            conditions[cIdx] = { ...cond, documents: cond.documents.filter((_, i) => i !== dIdx) };
                            setForm((f) => ({ ...f, conditions }));
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      onClick={() => {
                        const conditions = [...form.conditions];
                        conditions[cIdx] = { ...cond, documents: [...cond.documents, emptyDocRow()] };
                        setForm((f) => ({ ...f, conditions }));
                      }}
                    >
                      Add document to group
                    </Button>
                  </>
                ) : cond.type === 'complete_count_in_period' ? (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <TextField
                      select
                      size="small"
                      label="Item kind"
                      value={cond.itemKind}
                      onChange={(e) => {
                        const conditions = [...form.conditions];
                        conditions[cIdx] = { ...cond, itemKind: e.target.value as 'documents' | 'exercises' };
                        setForm((f) => ({ ...f, conditions }));
                      }}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="documents">documents</MenuItem>
                      <MenuItem value="exercises">exercises</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      label="Count"
                      type="number"
                      value={cond.count}
                      onChange={(e) => {
                        const conditions = [...form.conditions];
                        conditions[cIdx] = { ...cond, count: e.target.value };
                        setForm((f) => ({ ...f, conditions }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Period"
                      value={cond.period}
                      onChange={(e) => {
                        const conditions = [...form.conditions];
                        conditions[cIdx] = { ...cond, period: e.target.value as ConditionForm['period'] };
                        setForm((f) => ({ ...f, conditions }));
                      }}
                      sx={{ minWidth: 160 }}
                    >
                      {PERIODS.map((p) => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                      ))}
                    </TextField>
                    {cond.period === 'rolling_days' ? (
                      <TextField
                        size="small"
                        label="Days"
                        type="number"
                        value={cond.periodDays}
                        onChange={(e) => {
                          const conditions = [...form.conditions];
                          conditions[cIdx] = { ...cond, periodDays: e.target.value };
                          setForm((f) => ({ ...f, conditions }));
                        }}
                        sx={{ width: 80 }}
                      />
                    ) : null}
                    <TextField
                      size="small"
                      label="Min %"
                      type="number"
                      value={cond.minProgression}
                      onChange={(e) => {
                        const conditions = [...form.conditions];
                        conditions[cIdx] = { ...cond, minProgression: e.target.value };
                        setForm((f) => ({ ...f, conditions }));
                      }}
                      sx={{ width: 80 }}
                    />
                    {cond.itemKind === 'documents' ? (
                      <>
                        <TextField
                          select
                          size="small"
                          label="Domain filter"
                          value={cond.filterDomain}
                          onChange={(e) => {
                            const conditions = [...form.conditions];
                            conditions[cIdx] = { ...cond, filterDomain: e.target.value };
                            setForm((f) => ({ ...f, conditions }));
                          }}
                          sx={{ minWidth: 100 }}
                        >
                          <MenuItem value="">Any</MenuItem>
                          {DOC_DOMAINS.map((v) => (
                            <MenuItem key={v} value={v}>{v}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          label="Doc type"
                          value={cond.filterDocType}
                          onChange={(e) => {
                            const conditions = [...form.conditions];
                            conditions[cIdx] = { ...cond, filterDocType: e.target.value };
                            setForm((f) => ({ ...f, conditions }));
                          }}
                          sx={{ minWidth: 120 }}
                        />
                      </>
                    ) : null}
                  </Box>
                ) : cond.type === 'social_friends' ? (
                  <TextField
                    size="small"
                    label="Minimum friends"
                    type="number"
                    value={cond.minFriends}
                    onChange={(e) => {
                      const conditions = [...form.conditions];
                      conditions[cIdx] = { ...cond, minFriends: e.target.value };
                      setForm((f) => ({ ...f, conditions }));
                    }}
                    sx={{ width: 160 }}
                  />
                ) : cond.type === 'admin_verification' ? (
                  <TextField
                    size="small"
                    label="Verification kind"
                    value={cond.verificationKind}
                    onChange={(e) => {
                      const conditions = [...form.conditions];
                      conditions[cIdx] = { ...cond, verificationKind: e.target.value };
                      setForm((f) => ({ ...f, conditions }));
                    }}
                    helperText="e.g. google_review — completed manually by admin in user detail"
                    sx={{ minWidth: 280 }}
                  />
                ) : null}
              </Paper>
            ))}
            <Button
              size="small"
              onClick={() => setForm((f) => ({ ...f, conditions: [...f.conditions, emptyCondition()] }))}
            >
              Add condition
            </Button>

            <Typography variant="subtitle2">Rewards</Typography>
            <TextField
              label="Bonus XP"
              type="number"
              value={form.rewardXp}
              onChange={(e) => setForm((f) => ({ ...f, rewardXp: e.target.value }))}
              sx={{ maxWidth: 160 }}
            />
            <TextField
              select
              label="Reward badges"
              value=""
              onChange={(e) => {
                const id = e.target.value;
                if (id && !form.rewardBadgeIds.includes(id)) {
                  setForm((f) => ({ ...f, rewardBadgeIds: [...f.rewardBadgeIds, id] }));
                }
              }}
              helperText={
                form.rewardBadgeIds.length
                  ? form.rewardBadgeIds.map((id) => badges.find((b) => b._id === id)?.name || id).join(', ')
                  : 'None selected'
              }
            >
              <MenuItem value="">Add badge…</MenuItem>
              {badges.map((b) => (
                <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.locales.en.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
